/**
 * Wrapper do socket.io-client.
 *
 * Mantém uma conexão singleton com o backend e expõe helpers para
 * registrar/limpar listeners de eventos em tempo real (mensagens novas,
 * atualizações de conversa, etc.).
 *
 * O Socket.IO compartilha a MESMA porta do servidor web (HTTP/HTTPS),
 * então por padrão a URL é derivada de VUE_APP_APIURL.
 *
 * A conexão é autenticada pelo cookie httpOnly `access_token` no handshake
 * (`withCredentials: true`). O cliente não informa quem é — quem recusa ou
 * aceita é o servidor. Ver `backend/src/app/services/SocketService.js`.
 *
 * Uso:
 *   import socketClient from '@/services/socketClient';
 *
 *   socketClient.connect();
 *   socketClient.on('conversa:nova_mensagem', (payload) => { ... });
 *   socketClient.off('conversa:nova_mensagem', handler);
 */

import { io } from 'socket.io-client';
import resolveUrl from '@/services/resolveUrl';

/**
 * Resolve a URL do Socket.IO. Ordem de prioridade:
 *   1. VUE_APP_SOCKET_URL_HTTPS quando a página está em HTTPS
 *   2. VUE_APP_SOCKET_URL
 *   3. VUE_APP_APIURL (Socket.IO roda na mesma porta do servidor web)
 *
 * Quando a página está em HTTPS, força protocolo https:// (evita
 * mixed-content) sem mexer na porta — o backend anexa o Socket.IO ao
 * mesmo servidor HTTPS, então a porta é a do web.
 */
function resolveSocketUrl() {
  const paginaHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

  if (paginaHttps) {
    const explicit = resolveUrl(process.env.VUE_APP_SOCKET_URL_HTTPS);
    if (explicit) return explicit;
  }

  const base = resolveUrl(process.env.VUE_APP_SOCKET_URL)
    || resolveUrl(process.env.VUE_APP_APIURL);
  if (!base) return null;

  if (!paginaHttps) return base;

  try {
    const url = new URL(base);
    url.protocol = 'https:';
    return url.toString().replace(/\/$/, '');
  } catch (_e) {
    return base.replace(/^http:/, 'https:');
  }
}

let socket = null;
// Trava contra loop de reconexão quando o servidor recusa o handshake.
// Liberada por uma conexão bem-sucedida ou por `disconnect()` (logout).
let naoAutorizado = false;

function ensureSocket() {
  if (socket) return socket;
  const SOCKET_URL = resolveSocketUrl();
  if (!SOCKET_URL) {
    // eslint-disable-next-line no-console
    console.warn('[socketClient] VUE_APP_SOCKET_URL/VUE_APP_APIURL não configurados.');
    return null;
  }
  socket = io(SOCKET_URL, {
    transports: ['websocket', 'polling'],
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 2000,
    // Obrigatório: o backend autentica a conexão pelo cookie httpOnly
    // `access_token` enviado no handshake. Sem isso o transporte polling
    // conecta sem cookie e o servidor recusa.
    withCredentials: true,
  });

  // O servidor recusa handshake sem token válido (`unauthorized`). Reconectar
  // nesse caso é inútil: o cookie só volta a existir depois de novo login.
  // Sem esta guarda, a tela ficaria martelando o servidor a cada 2s.
  socket.on('connect_error', (err) => {
    if (err && err.message === 'unauthorized') {
      naoAutorizado = true;
      socket.disconnect();
    }
  });

  socket.on('connect', () => {
    naoAutorizado = false;
  });

  return socket;
}

export default {
  /**
   * Conecta ao servidor.
   *
   * A identidade da conexão vem do cookie httpOnly enviado no handshake — o
   * servidor não aceita mais o cliente se identificar. Os call sites antigos
   * ainda passam um `userId` — o argumento é simplesmente ignorado.
   */
  connect() {
    if (naoAutorizado) return null;
    const s = ensureSocket();
    if (!s) return null;
    if (!s.connected) s.connect();
    return s;
  },

  disconnect() {
    naoAutorizado = false;
    if (socket && socket.connected) socket.disconnect();
  },

  on(event, handler) {
    const s = ensureSocket();
    if (!s) return () => {};
    s.on(event, handler);
    return () => s.off(event, handler);
  },

  off(event, handler) {
    if (!socket) return;
    if (handler) socket.off(event, handler);
    else socket.off(event);
  },

  emit(event, payload) {
    const s = ensureSocket();
    if (!s) return;
    s.emit(event, payload);
  },

  isConnected() {
    return Boolean(socket && socket.connected);
  },
};
